import { useEffect, useState, useCallback, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { MapContainer, TileLayer } from 'react-leaflet';
import useIncidenciasPanel from '../../hooks/useIncidenciasPanel';
import useConfiguracion from '../../hooks/useConfiguracion';
import CapaMapaCalor from '../../components/Panel/CapaMapaCalor';
import PanelShell from './PanelShell';
import '../../styles/Panel.css';

const COLORES_ESTADO = {
  pendiente: '#f5a623',
  en_progreso: '#4a90d9',
  culminado: '#2ecc71',
  anulado: '#95a5a6',
};

// Paleta múltiple para categoría/zona (HU-010 Fase 4) — antes era una sola
// barra azul repetida; con ECharts cada barra rota un color de esta paleta,
// look "BI" en vez de un gráfico monocromático.
const PALETA_CATEGORICA = [
  '#1B5FA8', '#2ecc71', '#f5a623', '#e63946', '#9b59b6', '#16a085',
  '#e67e22', '#3498db', '#c0392b', '#8e44ad', '#27ae60', '#d35400',
];

const TEXTO_TOOLTIP = { color: '#14161A', fontSize: 12 };

const TABS = [
  { id: 'graficos', etiqueta: 'Gráficos', icono: 'fa-chart-column' },
  { id: 'mapas', etiqueta: 'Mapas', icono: 'fa-fire' },
  { id: 'comparar', etiqueta: 'Comparar', icono: 'fa-scale-balanced' },
];

const iso = (d) => d.toISOString().slice(0, 10);

// Presets de rango (HU-010 Fase 6) — el pedido era analizar "por día, por
// semana y por mes"; en vez de una agregación de backend distinta por cada
// granularidad, son atajos que calculan fecha_desde/fecha_hasta y llaman al
// mismo `estadisticas_incidencias` de siempre (ya acepta cualquier rango).
const calcularPreset = (preset) => {
  const hoy = new Date();
  if (preset === 'hoy') return { desde: iso(hoy), hasta: iso(hoy) };
  if (preset === 'ayer') {
    const d = new Date(hoy); d.setDate(d.getDate() - 1);
    return { desde: iso(d), hasta: iso(d) };
  }
  if (preset === 'semana') {
    const diaSemana = (hoy.getDay() + 6) % 7; // lunes=0
    const inicio = new Date(hoy); inicio.setDate(hoy.getDate() - diaSemana);
    return { desde: iso(inicio), hasta: iso(hoy) };
  }
  if (preset === 'semana_pasada') {
    const diaSemana = (hoy.getDay() + 6) % 7;
    const finPasada = new Date(hoy); finPasada.setDate(hoy.getDate() - diaSemana - 1);
    const inicioPasada = new Date(finPasada); inicioPasada.setDate(finPasada.getDate() - 6);
    return { desde: iso(inicioPasada), hasta: iso(finPasada) };
  }
  if (preset === 'mes') {
    const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    return { desde: iso(inicio), hasta: iso(hoy) };
  }
  if (preset === 'mes_pasado') {
    const inicio = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
    const fin = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
    return { desde: iso(inicio), hasta: iso(fin) };
  }
  return { desde: '', hasta: '' };
};

/**
 * Reportes del panel (HU-004 Fase 3; ampliado HU-010) — 3 pestañas:
 * "Gráficos" (por estado/categoría/zona, ECharts, Fase 4), "Mapas" (mapa de
 * calor de ubicación, Fase 5) y "Comparar" (dos periodos lado a lado, Fase
 * 6). Gráficos y Mapas comparten el mismo filtro de rango de fechas de
 * arriba; Comparar tiene su propio selector de dos periodos, con atajos de
 * día/semana/mes para no tener que tipear fechas a mano cada vez.
 */
const Reportes = () => {
  const { loading, error, obtenerEstadisticas, obtenerMapaCalor } = useIncidenciasPanel();
  const { configuracion } = useConfiguracion();

  const [tab, setTab] = useState('graficos');
  const [stats, setStats] = useState(null);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [puntosCalor, setPuntosCalor] = useState(null);

  const cargar = useCallback(async () => {
    const resultado = await obtenerEstadisticas({ fecha_desde: fechaDesde, fecha_hasta: fechaHasta });
    if (resultado.success) setStats(resultado.estadisticas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaDesde, fechaHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  // El mapa de calor solo se pide cuando esa pestaña está activa — no tiene
  // sentido traer todos los puntos si el administrador nunca la abre.
  useEffect(() => {
    if (tab !== 'mapas') return;
    obtenerMapaCalor({ fecha_desde: fechaDesde, fecha_hasta: fechaHasta }).then((r) => {
      if (r.success) setPuntosCalor(r.puntos);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, fechaDesde, fechaHasta]);

  const opcionEstado = useMemo(() => stats && {
    tooltip: { trigger: 'item', formatter: '{b}: <strong>{c}</strong> ({d}%)', textStyle: TEXTO_TOOLTIP },
    legend: { bottom: 0, icon: 'circle', textStyle: { color: '#14161A' } },
    series: [{
      name: 'Por estado',
      type: 'pie',
      radius: ['48%', '75%'],
      avoidLabelOverlap: true,
      itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 3 },
      label: { show: false },
      emphasis: {
        scaleSize: 8,
        label: { show: true, fontSize: 15, fontWeight: 700, formatter: '{b}\n{c}' },
      },
      data: stats.por_estado.map((e) => ({
        value: e.cantidad,
        name: e.estado_display,
        itemStyle: { color: COLORES_ESTADO[e.estado] || '#ccc' },
      })),
    }],
  }, [stats]);

  const opcionBarra = (lista, campoNombre) => lista && {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, textStyle: TEXTO_TOOLTIP },
    grid: { left: 140, right: 40, top: 10, bottom: 10, containLabel: true },
    xAxis: { type: 'value', axisLabel: { color: '#14161A' }, splitLine: { lineStyle: { color: '#f0eee9' } } },
    yAxis: {
      type: 'category',
      inverse: true,
      data: lista.map((item) => item[campoNombre] || 'Sin dato'),
      axisLabel: { color: '#14161A', fontSize: 12 },
    },
    series: [{
      type: 'bar',
      data: lista.map((item, i) => ({ value: item.cantidad, itemStyle: { color: PALETA_CATEGORICA[i % PALETA_CATEGORICA.length], borderRadius: [0, 6, 6, 0] } })),
      label: { show: true, position: 'right', color: '#14161A', fontWeight: 700 },
      barMaxWidth: 26,
    }],
  };

  const opcionCategoria = useMemo(() => opcionBarra(stats?.por_categoria, 'categoria'), [stats]);
  const opcionZona = useMemo(() => opcionBarra(stats?.por_zona, 'zona'), [stats]);

  const centroMapa = { lat: Number(configuracion.mapa_lat), lng: Number(configuracion.mapa_lng) };

  return (
    <PanelShell>
      <h2>Reportes</h2>

      <div className="panel-reportes-cabecera">
        <div className="panel-reportes-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`panel-reportes-tab ${tab === t.id ? 'panel-reportes-tab-activo' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <i className={`fas ${t.icono}`}></i> {t.etiqueta}
            </button>
          ))}
        </div>

        {(tab === 'graficos' || tab === 'mapas') && (
          <div className="panel-gu-filtros panel-reportes-filtro-fecha">
            <div>
              <label className="panel-label" style={{ margin: '0 0 4px' }}>Desde</label>
              <input type="date" className="panel-input" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
            </div>
            <div>
              <label className="panel-label" style={{ margin: '0 0 4px' }}>Hasta</label>
              <input type="date" className="panel-input" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
            </div>
            {(fechaDesde || fechaHasta) && (
              <button type="button" className="panel-boton-secundario" style={{ alignSelf: 'flex-end' }}
                onClick={() => { setFechaDesde(''); setFechaHasta(''); }}>
                Limpiar rango
              </button>
            )}
          </div>
        )}
      </div>

      {error && <p className="panel-gu-mensaje">{error}</p>}

      {tab === 'graficos' && (
        <>
          {loading && !stats && <p className="panel-texto-ayuda">Cargando reportes…</p>}
          {stats && (
            <>
              <div className="panel-alertas-grid" style={{ marginBottom: 28 }}>
                <div className="panel-alerta-tarjeta panel-alerta-tarjeta-total">
                  <span className="panel-alerta-cantidad">{stats.total_incidencias}</span>
                  <span className="panel-alerta-etiqueta">Total en el rango</span>
                </div>
                <div className="panel-alerta-tarjeta">
                  <span className="panel-alerta-cantidad">{stats.incidencias_ultimo_mes}</span>
                  <span className="panel-alerta-etiqueta">Últimos 30 días</span>
                </div>
                <div className="panel-alerta-tarjeta">
                  <span className="panel-alerta-cantidad">{stats.por_tipo.identificadas}</span>
                  <span className="panel-alerta-etiqueta">Identificadas</span>
                </div>
                <div className="panel-alerta-tarjeta">
                  <span className="panel-alerta-cantidad">{stats.por_tipo.anonimas}</span>
                  <span className="panel-alerta-etiqueta">Anónimas</span>
                </div>
              </div>

              <div className="panel-reportes-grid">
                <div className="panel-config-seccion">
                  <h3>Por estado</h3>
                  <ReactECharts option={opcionEstado} style={{ height: 320 }} notMerge />
                </div>

                <div className="panel-config-seccion">
                  <h3>Por categoría</h3>
                  <ReactECharts option={opcionCategoria} style={{ height: Math.max(240, (stats.por_categoria.length || 1) * 34) }} notMerge />
                </div>

                <div className="panel-config-seccion">
                  <h3>Por zona/distrito</h3>
                  <ReactECharts option={opcionZona} style={{ height: Math.max(240, (stats.por_zona.length || 1) * 34) }} notMerge />
                </div>
              </div>
            </>
          )}
        </>
      )}

      {tab === 'mapas' && (
        <div className="panel-config-seccion">
          <h3>Mapa de calor {(fechaDesde || fechaHasta) ? 'del rango elegido' : '(todo el histórico)'}</h3>
          {!puntosCalor && <p className="panel-texto-ayuda">Cargando puntos…</p>}
          {puntosCalor && puntosCalor.length === 0 && <p className="panel-texto-ayuda">No hay incidencias con ubicación en este rango.</p>}
          <div style={{ height: 480, borderRadius: 10, overflow: 'hidden' }}>
            <MapContainer center={centroMapa} zoom={13} style={{ height: '100%', width: '100%' }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
              {puntosCalor && puntosCalor.length > 0 && <CapaMapaCalor puntos={puntosCalor} />}
            </MapContainer>
          </div>
        </div>
      )}

      {tab === 'comparar' && <CompararPeriodos obtenerEstadisticas={obtenerEstadisticas} />}
    </PanelShell>
  );
};

/**
 * Pestaña "Comparar" (HU-010 Fase 6) — dos periodos independientes (A/B),
 * cada uno con atajos de día/semana/mes o fechas manuales, comparados en un
 * gráfico de barras agrupadas por estado + totales lado a lado. Reusa
 * `estadisticas_incidencias` dos veces (una por periodo) — no hizo falta
 * ningún endpoint de comparación en el backend, el filtro por rango ya
 * existía.
 */
const CompararPeriodos = ({ obtenerEstadisticas }) => {
  const [periodoA, setPeriodoA] = useState(calcularPreset('semana'));
  const [periodoB, setPeriodoB] = useState(calcularPreset('semana_pasada'));
  const [statsA, setStatsA] = useState(null);
  const [statsB, setStatsB] = useState(null);

  const cargarComparativa = useCallback(async () => {
    const [ra, rb] = await Promise.all([
      obtenerEstadisticas({ fecha_desde: periodoA.desde, fecha_hasta: periodoA.hasta }),
      obtenerEstadisticas({ fecha_desde: periodoB.desde, fecha_hasta: periodoB.hasta }),
    ]);
    if (ra.success) setStatsA(ra.estadisticas);
    if (rb.success) setStatsB(rb.estadisticas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodoA, periodoB]);

  useEffect(() => { cargarComparativa(); }, [cargarComparativa]);

  const ESTADOS_ORDEN = ['pendiente', 'en_progreso', 'culminado', 'anulado'];
  const nombrePorEstado = (lista, clave) => lista?.find((e) => e.estado === clave)?.estado_display || clave;
  const cantidadPorEstado = (lista, clave) => lista?.find((e) => e.estado === clave)?.cantidad || 0;

  const opcionComparativa = useMemo(() => (statsA && statsB) && {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, textStyle: TEXTO_TOOLTIP },
    legend: { bottom: 0, textStyle: { color: '#14161A' } },
    grid: { left: 50, right: 30, top: 20, bottom: 40, containLabel: true },
    xAxis: { type: 'category', data: ESTADOS_ORDEN.map((e) => nombrePorEstado(statsA.por_estado, e)), axisLabel: { color: '#14161A' } },
    yAxis: { type: 'value', axisLabel: { color: '#14161A' }, splitLine: { lineStyle: { color: '#f0eee9' } } },
    series: [
      {
        name: `Periodo A (${periodoA.desde} a ${periodoA.hasta})`,
        type: 'bar',
        data: ESTADOS_ORDEN.map((e) => cantidadPorEstado(statsA.por_estado, e)),
        itemStyle: { color: '#1B5FA8', borderRadius: [6, 6, 0, 0] },
      },
      {
        name: `Periodo B (${periodoB.desde} a ${periodoB.hasta})`,
        type: 'bar',
        data: ESTADOS_ORDEN.map((e) => cantidadPorEstado(statsB.por_estado, e)),
        itemStyle: { color: '#e63946', borderRadius: [6, 6, 0, 0] },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsA, statsB, periodoA, periodoB]);

  const SelectorPeriodo = ({ etiqueta, periodo, onCambiar }) => (
    <div className="panel-config-tarjeta">
      <span className="panel-config-tarjeta-titulo">{etiqueta}</span>
      <div className="panel-gu-acciones" style={{ marginTop: 10, marginBottom: 10 }}>
        <button type="button" className="panel-boton-secundario" onClick={() => onCambiar(calcularPreset('hoy'))}>Hoy</button>
        <button type="button" className="panel-boton-secundario" onClick={() => onCambiar(calcularPreset('ayer'))}>Ayer</button>
        <button type="button" className="panel-boton-secundario" onClick={() => onCambiar(calcularPreset('semana'))}>Esta semana</button>
        <button type="button" className="panel-boton-secundario" onClick={() => onCambiar(calcularPreset('semana_pasada'))}>Semana pasada</button>
        <button type="button" className="panel-boton-secundario" onClick={() => onCambiar(calcularPreset('mes'))}>Este mes</button>
        <button type="button" className="panel-boton-secundario" onClick={() => onCambiar(calcularPreset('mes_pasado'))}>Mes pasado</button>
      </div>
      <div className="panel-gu-fila-form">
        <div>
          <label className="panel-label">Desde</label>
          <input type="date" className="panel-input" value={periodo.desde} onChange={(e) => onCambiar({ ...periodo, desde: e.target.value })} />
        </div>
        <div>
          <label className="panel-label">Hasta</label>
          <input type="date" className="panel-input" value={periodo.hasta} onChange={(e) => onCambiar({ ...periodo, hasta: e.target.value })} />
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="panel-dashboard-fila-2col" style={{ marginTop: 16, marginBottom: 20 }}>
        <SelectorPeriodo etiqueta="Periodo A" periodo={periodoA} onCambiar={setPeriodoA} />
        <SelectorPeriodo etiqueta="Periodo B" periodo={periodoB} onCambiar={setPeriodoB} />
      </div>

      {statsA && statsB && (
        <>
          <div className="panel-alertas-grid" style={{ marginBottom: 24 }}>
            <div className="panel-alerta-tarjeta" style={{ '--color-estado': '#1B5FA8' }}>
              <span className="panel-alerta-cantidad">{statsA.total_incidencias}</span>
              <span className="panel-alerta-etiqueta">Total Periodo A</span>
            </div>
            <div className="panel-alerta-tarjeta" style={{ '--color-estado': '#e63946' }}>
              <span className="panel-alerta-cantidad">{statsB.total_incidencias}</span>
              <span className="panel-alerta-etiqueta">Total Periodo B</span>
            </div>
            <div className="panel-alerta-tarjeta">
              <span className="panel-alerta-cantidad">
                {statsA.total_incidencias - statsB.total_incidencias >= 0 ? '+' : ''}
                {statsA.total_incidencias - statsB.total_incidencias}
              </span>
              <span className="panel-alerta-etiqueta">Diferencia (A − B)</span>
            </div>
          </div>

          <div className="panel-config-seccion">
            <h3>Comparativa por estado</h3>
            <ReactECharts option={opcionComparativa} style={{ height: 360 }} notMerge />
          </div>
        </>
      )}
    </div>
  );
};

export default Reportes;
